import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { CreateUserDto, User } from '../../models/user.model';
import { DateRangePickerComponent } from '../../../../shared/date-range-picker/date-range-picker.component';
import { FormInputComponent } from '../../../../shared/forms/form-input/form-input.component';
import { FormTitleComponent } from '../../../../shared/forms/form-title/form-title.component';
import { generateRandomPassword } from '../../../../shared/utils/utils';
import { COUNTRY_CODES } from './country-codes';
import { ModalFormComponent } from '../../../../shared/forms/modal-form/modal-form.component';
import { UIInputModule } from '@homepower-co/shared-ui-angular';

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormInputComponent,
    FormTitleComponent,
    DateRangePickerComponent,
    MatIconModule,
    ModalFormComponent,
    UIInputModule,
  ],
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss', '../../../../shared/forms/form-layout.scss'],
})
export class UserFormComponent implements OnInit {
  isLoading = false;
  @Input() initialData: User | null = null; // Para edición, si hay datos
  @Output() createSubmit = new EventEmitter<CreateUserDto>();
  @Output() editSubmit = new EventEmitter<Partial<User>>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() formValidChange = new EventEmitter<boolean>();
  form!: FormGroup; // Inicialización para evitar error de compilación
  public countryCodes = COUNTRY_CODES;

  private fb = inject(FormBuilder);

  isModalReady = false; // Controlar cuándo mostrar el modal
  private initialFormValue: unknown = null; // Guardar valores iniciales
  hasChanges = false; // Detectar si hay cambios

  // // Control para el grupo
  // groupIdControl = new FormControl('', Validators.required);
  // // Opciones de grupo
  // groupOptions = [
  //   { label: 'Grupo 1', value: 'group1' },
  //   { label: 'Grupo 2', value: 'group2' },
  //   { label: 'Grupo 3', value: 'group3' },
  // ];

  get firstNameControl(): FormControl {
    return this.form.get('firstName') as FormControl;
  }
  get lastNameControl(): FormControl {
    return this.form.get('lastName') as FormControl;
  }
  get emailControl(): FormControl {
    return this.form.get('email') as FormControl;
  }
  get addressControl(): FormControl {
    return this.form.get('address') as FormControl;
  }
  get phoneControl(): FormControl {
    return this.form.get('phone') as FormControl;
  }
  get birthdateControl(): FormControl {
    return this.form.get('birthdate') as FormControl;
  }
  get isoCountryControl(): FormControl {
    return this.form.get('isoCountry') as FormControl;
  }

  // Método para saber si un campo tiene error
  hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  ngOnInit(): void {
    const data = this.initialData ?? ({} as User);
    // debug logs removed
    // Convertir birthdate a Date si es string y no está vacío
    let birthdateValue: Date | '' = '';
    if (data.birthdate) {
      birthdateValue =
        typeof data.birthdate === 'string'
          ? new Date(data.birthdate)
          : data.birthdate;
      // Si la conversión falla, dejarlo vacío
      if (birthdateValue instanceof Date && isNaN(birthdateValue.getTime())) {
        birthdateValue = '';
      }
    }
    // debug logs removed

    this.form = this.fb.group({
      firstName: [
        data.firstName ?? '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern('^[a-zA-ZÀ-ÿ\\s]{3,}$'),
        ],
      ],
      lastName: [
        data.lastName ?? '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern('^[a-zA-ZÀ-ÿ\\s]{3,}$'),
        ],
      ],
      birthdate: [
        birthdateValue,
        [Validators.required, UserFormComponent.birthdateValidator],
      ],
      isoCountry: [data.isoCountry ?? 'CO', Validators.required],
      phone: [
        data.phone ?? '',
        [Validators.required, Validators.pattern('^[0-9]{7,15}$')],
      ],
      email: [
        data.email ?? '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[a-zA-Z]{2,}$'),
        ],
      ],
      address: [data.address ?? '', Validators.required],
    });

    // En modo edición, remover required de campos que no tienen valor en el control
    if (this.initialData) {
      // Verificar el valor actual del control del formulario
      const firstNameValue = this.firstNameControl.value as string | null | undefined;
      const lastNameValue = this.lastNameControl.value as string | null | undefined;
      const birthdateValue = this.birthdateControl.value as Date | string | null | undefined;
      const isoCountryValue = this.isoCountryControl.value as string | null | undefined;
      const phoneValue = this.phoneControl.value as string | null | undefined;
      const emailValue = this.emailControl.value as string | null | undefined;
      const addressValue = this.addressControl.value as string | null | undefined;

      const hasFirstName = firstNameValue !== null && firstNameValue !== undefined && firstNameValue !== '';
      const hasLastName = lastNameValue !== null && lastNameValue !== undefined && lastNameValue !== '';
      const hasBirthdate = birthdateValue !== null && birthdateValue !== undefined && birthdateValue !== '';
      const hasIsoCountry = isoCountryValue !== null && isoCountryValue !== undefined && isoCountryValue !== '';
      const hasPhone = phoneValue !== null && phoneValue !== undefined && phoneValue !== '';
      const hasEmail = emailValue !== null && emailValue !== undefined && emailValue !== '';
      const hasAddress = addressValue !== null && addressValue !== undefined && addressValue !== '';

      if (!hasFirstName) {
        this.firstNameControl.clearValidators();
        this.firstNameControl.setValidators([Validators.minLength(3), Validators.pattern('^[a-zA-ZÀ-ÿ\\s]{3,}$')]);
        this.firstNameControl.updateValueAndValidity();
      }
      if (!hasLastName) {
        this.lastNameControl.clearValidators();
        this.lastNameControl.setValidators([Validators.minLength(3), Validators.pattern('^[a-zA-ZÀ-ÿ\\s]{3,}$')]);
        this.lastNameControl.updateValueAndValidity();
      }
      if (!hasBirthdate) {
        this.birthdateControl.clearValidators();
        this.birthdateControl.setValidators([UserFormComponent.birthdateValidator]);
        this.birthdateControl.updateValueAndValidity();
      }
      if (!hasIsoCountry) {
        this.isoCountryControl.clearValidators();
        this.isoCountryControl.updateValueAndValidity();
      }
      if (!hasPhone) {
        this.phoneControl.clearValidators();
        this.phoneControl.setValidators([Validators.pattern('^[0-9]{7,15}$')]);
        this.phoneControl.updateValueAndValidity();
      }
      if (!hasEmail) {
        this.emailControl.clearValidators();
        this.emailControl.setValidators([Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[a-zA-Z]{2,}$')]);
        this.emailControl.updateValueAndValidity();
      }
      if (!hasAddress) {
        this.addressControl.clearValidators();
        this.addressControl.updateValueAndValidity();
      }
    }

    if (this.initialData?.userId) {
      this.form.get('birthdate')?.disable({ emitEvent: false });
    }

    // this.form.addControl('groupId', this.groupIdControl);

    // Guardar valores iniciales para comparar cambios (solo en modo edición)
    if (this.initialData) {
      this.initialFormValue = this.getComparableFormValue();
      this.hasChanges = false;
      this.form.valueChanges.subscribe(() => {
        const current = this.getComparableFormValue();
        this.hasChanges =
          JSON.stringify(current) !== JSON.stringify(this.initialFormValue);
      });
    } else {
      this.hasChanges = true;
    }

    // Mostrar modal después de que todo esté inicializado
    setTimeout(() => {
      this.isModalReady = true;
    }, 50);

    // Emitir cambios en la validez del formulario
    this.form.statusChanges.subscribe(() => {
      this.formValidChange.emit(this.form.valid);
    });
    // Emitir estado inicial
    this.formValidChange.emit(this.form.valid);
  }

  // Método para debugging - ver qué campos tienen errores
  getFormErrors() {
    const errors: Record<string, unknown> = {};
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  static birthdateValidator(control: import('@angular/forms').AbstractControl) {
    const value = control.value;
    if (!value) return null;
    const d = new Date(value);
    const today = new Date();
    if (isNaN(d.getTime())) return { invalidDate: true };
    if (d > today) return { futureDate: true };
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age >= 18 ? null : { underage: true };
  }

  // Método para obtener el mensaje de error de un campo
  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.errors) return '';
    if (control.errors['required']) return 'Este campo es obligatorio.';
    if (control.errors['minlength'])
      return `Mínimo ${control.errors['minlength'].requiredLength} caracteres.`;
    if (control.errors['pattern']) return 'Formato inválido.';
    if (control.errors['email']) return 'Correo electrónico inválido.';
    if (control.errors['futureDate']) return 'No puede ser una fecha futura.';
    if (control.errors['underage']) return 'Debe ser mayor de edad.';
    return 'Campo inválido.';
  }
  isNumberKey(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Home',
      'End',
    ];
    if (
      allowedKeys.indexOf(event.key) !== -1 ||
      (event.key >= '0' && event.key <= '9')
    ) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  onPhoneInputChange(event: Event): void {
    if (event instanceof CustomEvent) {
      event.stopPropagation();
      const val = event.detail as string | number;
      this.phoneControl.setValue(String(val ?? ''), { emitEvent: true });
      this.phoneControl.markAsDirty();
    }
  }

  onPhoneInputBlur(event: Event): void {
    event.stopPropagation();
    this.phoneControl.markAsTouched();
  }
  errorDuplicateUser = false;

  private getComparableFormValue(): Record<string, unknown> {
    const raw = this.form.getRawValue() as Record<string, unknown>;
    const bd = raw['birthdate'];
    let normalized = '';
    if (bd instanceof Date && !isNaN(bd.getTime())) {
      normalized = bd.toISOString().split('T')[0];
    } else if (typeof bd === 'string' && bd) {
      normalized = bd.split('T')[0];
    }
    return { ...raw, birthdate: normalized };
  }

  onSubmit(): void {
    if (this.form.invalid || this.isLoading) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorDuplicateUser = false;
    const randomPassword = generateRandomPassword(12);
    const birthdate = this.form.value.birthdate;
    let formattedBirthdate = '';

    if (birthdate instanceof Date) {
      formattedBirthdate = birthdate.toISOString().split('T')[0];
    } else if (typeof birthdate === 'string' && birthdate) {
      formattedBirthdate = birthdate.split('T')[0];
    }

    if (this.initialData) {
      // Modo edición: emitir campos permitidos por el backend según schema PATCH
      const allowedData: Partial<User> = {
        firstName: String(this.form.value.firstName ?? ''),
        lastName: String(this.form.value.lastName ?? ''),
        phone: String(
          this.form.value.phone ?? String(this.initialData.phone ?? ''),
        ),
        isoCountry: String(this.form.value.isoCountry ?? 'CO'),
        address: String(this.form.value.address ?? ''),
        location_number: String(
          this.form.value.location_number ??
            String(this.initialData.location_number ?? ''),
        ),
        email: String(this.form.value.email ?? ''),
      };
      this.editSubmit.emit(allowedData);
    } else {
      // Modo creación: emitir CreateUserDto y llamar al servicio
      const userData: CreateUserDto = {
        username: String(this.form.value.email ?? ''),
        email: String(this.form.value.email ?? ''),
        password: String(randomPassword),
        firstName: String(this.form.value.firstName ?? ''),
        lastName: String(this.form.value.lastName ?? ''),
        phone: String(this.form.value.phone ?? ''),
        isoCountry: String(this.form.value.isoCountry ?? 'CO'),
        address: String(this.form.value.address ?? ''),
        birthdate: String(formattedBirthdate ?? ''),
        location_number: String(this.form.value.location_number ?? '454654564'),
      };
      this.createSubmit.emit(userData);
    }
  }

  cancelar(): void {
    this.cancelled.emit();
  }
}
